import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { N11CatalogProduct } from './N11ProductUploadModal';
import { n11Api } from '../../../config/apiClient';

interface N11ProductCreateModalProps {
  open: boolean;
  onClose: () => void;
  product: N11CatalogProduct | null;
  companyId: string | null;
  onSaveLog?: (logEntry: {
    type: 'price-stock-update' | 'product-create' | 'product-save';
    productName: string;
    stockCode: string;
    taskId: number;
    status: 'IN_QUEUE' | 'PROCESSED' | 'REJECT';
    operation: string;
    details: any;
  }) => void;
}

const N11ProductCreateModal: React.FC<N11ProductCreateModalProps> = ({
  open,
  onClose,
  product,
  companyId,
  onSaveLog
}) => {
  // State'ler
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state'leri
  const [formData, setFormData] = useState({
    description: '',
    productMainId: '',
    preparingDay: 2,
    shipmentTemplate: 'BAREM',
    maxPurchaseQuantity: 5,
    stockCode: '',
    quantity: 1,
    salePrice: '',
    listPrice: '',
    vatRate: 18
  });

  // Form temizleme
  const handleClearForm = () => {
    setFormData({
      description: '',
      productMainId: '',
      preparingDay: 2,
      shipmentTemplate: 'BAREM',
      maxPurchaseQuantity: 5,
      stockCode: '',
      quantity: 1,
      salePrice: '',
      listPrice: '',
      vatRate: 18
    });
    setError(null);
    setSuccess(null);
  };

  // Modal kapanırken temizle
  const handleClose = () => {
    handleClearForm();
    onClose();
  };

  // Form değişiklik handler'ı
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Ürünü N11'e gönder
  const handleCreateProduct = async () => {
    if (!companyId || !product) {
      setError('Gerekli bilgiler eksik');
      return;
    }

    // Form validasyonu
    if (!formData.stockCode.trim()) {
      setError('Stok kodu zorunludur');
      return;
    }
    if (!formData.salePrice || parseFloat(formData.salePrice) <= 0) {
      setError('Geçerli bir satış fiyatı giriniz');
      return;
    }
    if (!formData.listPrice || parseFloat(formData.listPrice) <= 0) {
      setError('Geçerli bir liste fiyatı giriniz');
      return;
    }
    if (parseFloat(formData.salePrice) > parseFloat(formData.listPrice)) {
      setError('Satış fiyatı liste fiyatından büyük olamaz');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('🚀 [N11 PRODUCT CREATE] Ürün oluşturma başlatılıyor...', product);

      // Fiyat hesaplama debug
      console.log('💰 [N11 PRODUCT CREATE] Fiyat Debug:');
      console.log(`  - Form Satış Fiyatı: "${formData.salePrice}" (tip: ${typeof formData.salePrice})`);
      console.log(`  - Form Liste Fiyatı: "${formData.listPrice}" (tip: ${typeof formData.listPrice})`);
      console.log(`  - parseFloat(salePrice): ${parseFloat(formData.salePrice)}`);
      console.log(`  - parseFloat(listPrice): ${parseFloat(formData.listPrice)}`);
      console.log(`  - TL Satış: ${parseFloat(parseFloat(formData.salePrice).toFixed(2))}`);
      console.log(`  - TL Liste: ${parseFloat(parseFloat(formData.listPrice).toFixed(2))}`);

      // API payload'ını hazırla
      const payload = {
        integrator: "GTS E-Ticaret Sistemi",
        skus: [
          {
            description: formData.description.trim() || product.productTitle,
            categoryId: parseInt(product.categoryId),
            currencyType: "TL", // Para birimi - N11 için zorunlu
            productMainId: formData.productMainId.trim() || `GTS-${product.catalogId}`,
            preparingDay: formData.preparingDay,
            shipmentTemplate: formData.shipmentTemplate,
            maxPurchaseQuantity: formData.maxPurchaseQuantity,
            stockCode: formData.stockCode.trim(),
            catalogId: product.catalogId, // Katalog ID'si ile ürün ekliyoruz
            barcode: null, // catalogId varsa barcode null
            quantity: formData.quantity,
            images: [], // Zorunlu boş array
            attributes: [], // Zorunlu boş array
            salePrice: parseFloat(formData.salePrice), // TL cinsinden
            listPrice: parseFloat(formData.listPrice), // TL cinsinden
            vatRate: formData.vatRate
          }
        ]
      };

      console.log('📦 [N11 PRODUCT CREATE] Payload:', payload);

      // Backend API'ye istek gönder
      const response = await n11Api.post('/api/n11/products/create', payload);

      const data = response.data;
      
      if (!data.success) {
        throw new Error(data.error || 'Ürün oluşturma işlemi başarısız');
      }

      console.log('✅ [N11 PRODUCT CREATE] Ürün oluşturma başarılı:', data);
      
      // Log kaydet
      if (onSaveLog && product) {
        onSaveLog({
          type: 'product-create',
          productName: product.productTitle,
          stockCode: formData.stockCode,
          taskId: data.taskId,
          status: data.status,
          operation: `Katalog ürünü sisteme eklendi - Katalog ID: ${product.catalogId}`,
          details: {
            catalogId: product.catalogId,
            categoryId: product.categoryId,
            categoryName: product.categoryName,
            salePrice: formData.salePrice,
            listPrice: formData.listPrice,
            quantity: formData.quantity,
            vatRate: formData.vatRate,
            preparingDay: formData.preparingDay,
            shipmentTemplate: formData.shipmentTemplate
          }
        });
      }
      
      setSuccess(`Ürün başarıyla N11'e gönderildi! 
      
Task ID: ${data.taskId}
Status: ${data.status}
${data.reasons ? data.reasons.join(', ') : ''}

Ürün işleme alındı. Durumunu N11 satıcı panelinden kontrol edebilirsiniz.`);

    } catch (error) {
      console.error('❌ [N11 PRODUCT CREATE] Ürün oluşturma hatası:', error);
      setError(error instanceof Error ? error.message : 'Ürün oluşturma sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'success.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCartIcon />
          <Typography variant="h6">N11 Ürün Satışa Aç</Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Ürün Bilgisi */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Seçilen Ürün:</strong> {product.productTitle}
          </Typography>
          <Typography variant="caption" display="block">
            Katalog ID: {product.catalogId} | Kategori: {product.categoryName}
          </Typography>
        </Alert>

        {/* Hata/Başarı Mesajları */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
              {success}
            </pre>
          </Alert>
        )}

        {/* Form */}
        <Grid container spacing={3}>
          {/* Sol Kolon */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Stok Kodu *"
              value={formData.stockCode}
              onChange={(e) => handleInputChange('stockCode', e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Ürün Ana ID"
              value={formData.productMainId}
              onChange={(e) => handleInputChange('productMainId', e.target.value)}
              disabled={loading}
              helperText="Boş bırakılırsa otomatik oluşturulur"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Açıklama"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={loading}
              helperText="Boş bırakılırsa katalog açıklaması kullanılır"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Hazırlık Süresi (Gün)"
              value={formData.preparingDay}
              onChange={(e) => handleInputChange('preparingDay', parseInt(e.target.value) || 0)}
              disabled={loading}
              sx={{ mb: 2 }}
            />
          </Grid>

          {/* Sağ Kolon */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Satış Fiyatı (TL) *"
              value={formData.salePrice}
              onChange={(e) => handleInputChange('salePrice', e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start">₺</InputAdornment>,
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Liste Fiyatı (TL) *"
              value={formData.listPrice}
              onChange={(e) => handleInputChange('listPrice', e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start">₺</InputAdornment>,
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Stok Miktarı"
              value={formData.quantity}
              onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 0)}
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>KDV Oranı (%)</InputLabel>
                  <Select
                    value={formData.vatRate}
                    label="KDV Oranı (%)"
                    onChange={(e) => handleInputChange('vatRate', e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value={0}>%0</MenuItem>
                    <MenuItem value={1}>%1</MenuItem>
                    <MenuItem value={8}>%8</MenuItem>
                    <MenuItem value={18}>%18</MenuItem>
                    <MenuItem value={20}>%20</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Satın Alma"
                  value={formData.maxPurchaseQuantity}
                  onChange={(e) => handleInputChange('maxPurchaseQuantity', parseInt(e.target.value) || 0)}
                  disabled={loading}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Kargo Şablonu"
              value={formData.shipmentTemplate}
              onChange={(e) => handleInputChange('shipmentTemplate', e.target.value)}
              disabled={loading}
              helperText="N11 panelinizdeki kargo şablon adı"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Button
          onClick={handleClearForm}
          disabled={loading}
          color="inherit"
        >
          Temizle
        </Button>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          İptal
        </Button>
        <Button
          onClick={handleCreateProduct}
          disabled={loading || !formData.stockCode.trim() || !formData.salePrice || !formData.listPrice}
          variant="contained"
          color="success"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartIcon />}
        >
          {loading ? 'Gönderiliyor...' : 'Ürünü Satışa Aç'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default N11ProductCreateModal;
